import { useState } from "react";
import { MRT_RowData, MRT_TableInstance } from "material-react-table";
import {
  Button,
  FormLabel,
  Radio,
  RadioGroup,
  Space,
  TextField,
  Autocomplete,
  IconButton,
  DropdownMenu,
  useMediaQuery,
  Dialog,
  Badge,
  DatePicker,
  DateTimePicker,
} from "@techzyapp/ui-lib";
import dayjs from "dayjs";

interface TableFilterProps<T extends MRT_RowData> {
  table: MRT_TableInstance<T>;
  columnFilters?: any;
  resetPagination: () => void;
}

const TableFilter = <T extends MRT_RowData>({
  table,
  columnFilters,
  resetPagination,
}: TableFilterProps<T>) => {
  const sm = useMediaQuery("sm");
  const xxs = useMediaQuery("xxs");

  const [dialogOpen, setDialogOpen] = useState(false);

  const handleDialogOpen = () => setDialogOpen(true);
  const handleDialogClose = () => setDialogOpen(false);

  const result = columnFilters.reduce((acc: any, item: any) => {
    acc[item.id] = item.value;
    return acc;
  }, {});

  const [filterValues, setFilterValues] =
    useState<Record<string, string | boolean | string[]>>(result);

  const { getHeaderGroups } = table;

  const handleFilter = () => {
    Object.entries(filterValues).forEach(([id, value]) => {
      const column = table.getColumn(id);
      column.setFilterValue(value || undefined);
    });

    resetPagination();
  };

  const handleClear = () => {
    setFilterValues({});
    table.getAllColumns().forEach((column) => {
      column.setFilterValue(undefined);
    });
    resetPagination();
  };

  const Filter = () => (
    <div>
      {getHeaderGroups().map((headerGroup) =>
        headerGroup.headers.map((header) => {
          if (!header.column.getCanFilter()) return null;

          const filterVariant = header.column.columnDef.filterVariant;

          switch (filterVariant) {
            case "text":
              return (
                <Space key={header.id}>
                  <TextField
                    label={`Filter by ${header.column.columnDef.header}`}
                    value={(filterValues[header.id] as string) || ""}
                    onChange={(e) =>
                      setFilterValues((prev) => ({
                        ...prev,
                        [header.id]: e.target.value,
                      }))
                    }
                  />
                </Space>
              );

            case "checkbox":
              const checkboxLabels = (
                header.column.columnDef as {
                  checkboxLabels?: [string, string];
                }
              ).checkboxLabels;

              return (
                <Space key={header.id}>
                  <FormLabel>{`Filter by ${header.column.columnDef.header}`}</FormLabel>
                  <RadioGroup
                    row
                    value={filterValues[header.id] as string}
                    onChange={(e) => {
                      setFilterValues((prev) => ({
                        ...prev,
                        [header.id]: e.target.value,
                      }));
                    }}
                  >
                    <Radio
                      label="All"
                      value=""
                      checked={
                        filterValues[header.id] === "" ||
                        filterValues[header.id] === undefined
                      }
                    />
                    <Radio
                      label={checkboxLabels ? checkboxLabels[0] : ""}
                      value="true"
                      checked={filterValues[header.id] === "true"}
                    />
                    <Radio
                      label={checkboxLabels ? checkboxLabels[1] : ""}
                      value="false"
                      checked={filterValues[header.id] === "false"}
                    />
                  </RadioGroup>
                </Space>
              );

            case "select":
            case "autocomplete":
              return (
                <Space key={header.id}>
                  <Autocomplete
                    label={`Filter by ${header.column.columnDef.header}`}
                    value={
                      header.column.columnDef.filterSelectOptions?.find(
                        (option) =>
                          typeof option === "object" &&
                          "value" in option &&
                          option.value === filterValues[header.id]
                      ) || null
                    }
                    options={header.column.columnDef.filterSelectOptions || []}
                    multiple={false}
                    onChange={(e, value) => {
                      setFilterValues((prev) => ({
                        ...prev,
                        [header.id]:
                          value && typeof value === "object" && "value" in value
                            ? value.value
                            : null,
                      }));
                    }}
                  />
                </Space>
              );

            case "multi-select":
              return (
                <Space key={header.id}>
                  <Autocomplete
                    label={`Filter by ${header.column.columnDef.header}`}
                    value={
                      header.column.columnDef.filterSelectOptions?.filter(
                        (option) => {
                          if (Array.isArray(filterValues[header.id])) {
                            return (
                              typeof option === "object" &&
                              option !== null &&
                              "value" in option &&
                              (filterValues[header.id] as string[]).includes(
                                option.value
                              )
                            );
                          }
                          return false;
                        }
                      ) || []
                    }
                    options={header.column.columnDef.filterSelectOptions || []}
                    multiple={true}
                    onChange={(e, value) => {
                      const newValues = Array.isArray(value)
                        ? value
                            .map((val) =>
                              typeof val === "object" && "value" in val
                                ? val.value
                                : null
                            )
                            .filter(Boolean)
                        : [];
                      setFilterValues((prev) => ({
                        ...prev,
                        [header.id]: newValues,
                      }));
                    }}
                  />
                </Space>
              );

            case "date":
              return (
                <Space key={header.id}>
                  <DatePicker
                    label={`Filter by ${header.column.columnDef.header}`}
                    value={
                      filterValues[header.id]
                        ? dayjs(filterValues[header.id] as string)
                        : null
                    }
                    onChange={(date) => {
                      setFilterValues((prev) => ({
                        ...prev,
                        [header.id]: date ? date.toISOString() : "",
                      }));
                    }}
                  />
                </Space>
              );

            case "date-range":
              return (
                <div key={header.id}>
                  <Space key={header.id}>
                    <DatePicker
                      label={`Filter by ${header.column.columnDef.header} (From)`}
                      value={
                        filterValues[`${header.id}From`]
                          ? dayjs(filterValues[`${header.id}From`] as string)
                          : null
                      }
                      onChange={(date) => {
                        setFilterValues((prev) => ({
                          ...prev,
                          [`${header.id}From`]: date ? date.toISOString() : "",
                        }));
                      }}
                    />
                  </Space>
                  <Space>
                    <DatePicker
                      label={`Filter by ${header.column.columnDef.header} (To)`}
                      value={
                        filterValues[`${header.id}To`]
                          ? dayjs(filterValues[`${header.id}To`] as string)
                          : null
                      }
                      onChange={(date) => {
                        setFilterValues((prev) => ({
                          ...prev,
                          [`${header.id}To`]: date ? date.toISOString() : "",
                        }));
                      }}
                      minDate={
                        filterValues[`${header.id}From`]
                          ? dayjs(filterValues[`${header.id}From`] as string)
                          : undefined
                      }
                    />
                  </Space>
                </div>
              );

            case "datetime":
              return (
                <Space key={header.id}>
                  <DateTimePicker
                    label={`Filter by ${header.column.columnDef.header}`}
                    value={
                      filterValues[header.id]
                        ? dayjs(filterValues[header.id] as string)
                        : null
                    }
                    onChange={(date) => {
                      setFilterValues((prev) => ({
                        ...prev,
                        [header.id]: date ? date.toISOString() : "",
                      }));
                    }}
                  />
                </Space>
              );

            case "datetime-range":
              return (
                <div key={header.id}>
                  <Space key={header.id}>
                    <DateTimePicker
                      label={`Filter by ${header.column.columnDef.header} (From)`}
                      value={
                        filterValues[`${header.id}From`]
                          ? dayjs(filterValues[`${header.id}From`] as string)
                          : null
                      }
                      onChange={(date) => {
                        setFilterValues((prev) => ({
                          ...prev,
                          [`${header.id}From`]: date ? date.toISOString() : "",
                        }));
                      }}
                    />
                  </Space>
                  <Space>
                    <DateTimePicker
                      label={`Filter by ${header.column.columnDef.header} (To)`}
                      value={
                        filterValues[`${header.id}To`]
                          ? dayjs(filterValues[`${header.id}To`] as string)
                          : null
                      }
                      onChange={(date) => {
                        setFilterValues((prev) => ({
                          ...prev,
                          [`${header.id}To`]: date ? date.toISOString() : "",
                        }));
                      }}
                      minDate={
                        filterValues[`${header.id}From`]
                          ? dayjs(filterValues[`${header.id}From`] as string)
                          : undefined
                      }
                    />
                  </Space>
                </div>
              );

            case "range":
              return (
                <div key={header.id}>
                  <Space key={header.id}>
                    <TextField
                      type="number"
                      label={`Filter by ${header.column.columnDef.header} (From)`}
                      value={(filterValues[`${header.id}From`] as string) || ""}
                      onChange={(e) =>
                        setFilterValues((prev) => ({
                          ...prev,
                          [`${header.id}From`]: e.target.value,
                        }))
                      }
                    />
                  </Space>
                  <Space>
                    <TextField
                      type="number"
                      label={`Filter by ${header.column.columnDef.header} (To)`}
                      value={(filterValues[`${header.id}To`] as string) || ""}
                      onChange={(e) =>
                        setFilterValues((prev) => ({
                          ...prev,
                          [`${header.id}To`]: e.target.value,
                        }))
                      }
                    />
                  </Space>
                </div>
              );

            default:
              return null;
          }
        })
      )}
    </div>
  );

  return (
    <>
      {sm ? (
        <>
          <Badge badgeContent={columnFilters.length} color="medium">
            <IconButton
              iconName="Filter"
              variant="filled"
              onClick={handleDialogOpen}
            />
          </Badge>
          <Dialog
            fullScreen={xxs}
            open={dialogOpen}
            onClose={handleDialogClose}
            title="Filter"
            size="sm"
            actions={
              <>
                <div className="Mui-tableFilter-clear-action">
                  <Button
                    onClick={() => {
                      handleClear();
                      handleDialogClose();
                    }}
                    variant="filled"
                    color="primary"
                    iconName="Trash"
                  >
                    Clear
                  </Button>
                </div>
                <Button
                  iconName="Filter"
                  iconVariant="Bold"
                  onClick={() => {
                    handleFilter();
                    handleDialogClose();
                  }}
                >
                  Filter
                </Button>
              </>
            }
          >
            <div>{Filter()}</div>
          </Dialog>
        </>
      ) : (
        <DropdownMenu
          title="Filter"
          icon="Filter"
          component={
            <Badge badgeContent={columnFilters.length} color="medium">
              <IconButton iconName="Filter" variant="filled" />
            </Badge>
          }
          actionButton={{
            label: "Filter",
            onClick: handleFilter,
            closeOnClick: true,
            iconName: "Filter",
            iconVariant: "Bold",
          }}
          secondaryActionButton={{
            label: "Clear",
            variant: "filled",
            color: "primary",
            onClick: handleClear,
            closeOnClick: true,
            iconName: "Trash",
          }}
        >
          {Filter()}
        </DropdownMenu>
      )}
    </>
  );
};

export default TableFilter;
